import { useState, useEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';
import { RestaurantCard } from './RestaurantCard';
import { useLanguage } from '@/contexts/AppContext';
import { logger } from '@/utils/logger';

interface Store {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  isOpen: boolean;
  distance?: string;
  rating: number;
  imageUrl: string;
}

interface StoreMapModalProps {
  isOpen: boolean;
  onClose: () => void;
  stores: Store[];
  onStoreSelect: (storeId: string) => void;
}

const translations = {
  ko: {
    title: '지도 기반 매장 탐색',
    myLocation: '내 위치',
    open: '영업중',
    closed: '마감',
    viewMenu: '메뉴 보기',
    noLocation: '위치 정보를 가져올 수 없습니다',
    distance: 'km',
    rating: '별점',
    nearest: '가까운순',
    highestRated: '평점순',
    openNow: '영업중만',
    allStores: '모든 매장',
    noStores: '매장을 찾을 수 없습니다'
  },
  es: {
    title: 'Explorador de tiendas basado en mapa',
    myLocation: 'Mi ubicación',
    open: 'Abierto',
    closed: 'Cerrado',
    viewMenu: 'Ver menú',
    noLocation: 'No se puede obtener la ubicación',
    distance: 'km',
    rating: 'Calificación',
    nearest: 'Más cercano',
    highestRated: 'Mejor valorado',
    openNow: 'Solo abiertos',
    allStores: 'Todas las tiendas',
    noStores: 'No se encontraron tiendas'
  },
  en: {
    title: 'Map-based Store Finder',
    myLocation: 'My Location',
    open: 'Open',
    closed: 'Closed',
    viewMenu: 'View Menu',
    noLocation: 'Cannot get location',
    distance: 'km',
    rating: 'Rating',
    nearest: 'Nearest',
    highestRated: 'Highest rated',
    openNow: 'Open now',
    allStores: 'All stores',
    noStores: 'No stores found'
  }
};

export function StoreMapModal({ isOpen, onClose, stores, onStoreSelect }: StoreMapModalProps) {
  const { language } = useLanguage();
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [_locationError, setLocationError] = useState<string | null>(null);
  const [sortBy, _setSortBy] = useState<'distance' | 'rating'>('distance');
  const [filterOpen, _setFilterOpen] = useState(false);
  const _mapRef = useRef<HTMLDivElement>(null);

  const t = translations[language];

  useEffect(() => {
    if (isOpen) {
      // Try to get user's location
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
            setLocationError(null);
          },
          (error) => {
            logger.error('Error getting location:', error);
            setLocationError(t.noLocation);
            // Default to Seoul if location not available
            setUserLocation({
              lat: 37.5665,
              lng: 126.9780
            });
          }
        );
      }
    }
  }, [isOpen, t.noLocation]);

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c; // Distance in km
    return d.toFixed(1);
  };

  const deg2rad = (deg: number) => {
    return deg * (Math.PI / 180);
  };

  // Filter and sort stores
  const filteredStores = stores
    .filter(store => {
      const matchesFilter = !filterOpen || store.isOpen;
      return matchesFilter;
    })
    .map(store => {
      let calculatedDistance = 0;
      if (userLocation) {
        calculatedDistance = parseFloat(calculateDistance(
          userLocation.lat,
          userLocation.lng,
          store.lat,
          store.lng
        ));
      }
      return { ...store, calculatedDistance };
    })
    .sort((a, b) => {
      if (sortBy === 'distance') {
        return a.calculatedDistance - b.calculatedDistance;
      } else {
        return b.rating - a.rating;
      }
    });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full h-full max-w-full max-h-full bg-background overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-primary h-[50px] relative">
          <div className="flex items-center justify-center h-full px-[20px]">
            {/* Back Button - Absolute Left */}
            <button
              onClick={onClose}
              className="absolute left-[20px] flex items-center justify-center w-[32px] h-[32px]"
            >
              <svg className="w-[12px] h-[21px]" fill="none" preserveAspectRatio="none" viewBox="0 0 12 21">
                <path d="M11.588 0.41098C11.4583 0.280705 11.3042 0.177346 11.1345 0.106823C10.9649 0.0363008 10.783 0 10.5993 0C10.4156 0 10.2337 0.0363008 10.0641 0.106823C9.89442 0.177346 9.74031 0.280705 9.61059 0.41098L0.326717 9.71087C0.223149 9.8144 0.140982 9.93738 0.0849197 10.0728C0.0288573 10.2082 0 10.3533 0 10.4999C0 10.6464 0.0288573 10.7916 0.0849197 10.9269C0.140982 11.0623 0.223149 11.1853 0.326717 11.2888L9.61059 20.5887C10.158 21.1371 11.0406 21.1371 11.588 20.5887C12.1354 20.0404 12.1354 19.1562 11.588 18.6079L3.49955 10.4943L11.5992 2.38063C12.1354 1.84345 12.1354 0.948157 11.588 0.41098Z" fill="white" />
              </svg>
            </button>
            
            {/* Title - Center */}
            <h2 className="font-['Noto_Sans:Bold',_'Noto_Sans_KR:Bold',_sans-serif] font-bold text-[18px] text-white tracking-[-0.36px] uppercase" style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}>
              {t.title}
            </h2>
          </div>
        </div>

        {/* Map Container - 30% height */}
        <div className="h-[30%] relative overflow-hidden border-b border-border">
          {/* Google Maps Embed */}
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3164.289!2d126.97797!3d37.56654!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzfCsDMzJzU5LjUiTiAxMjbCsDU4JzQwLjciRQ!5e0!3m2!1sen!2skr!4v1234567890"
            width="100%"
            height="100%"
            style={{ border: 0, pointerEvents: 'none' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full h-full"
          />
        </div>

        {/* Store List - 50% height */}
        <div className="flex-1 overflow-y-auto bg-muted">
          <div className="p-[min(5vw,1.25rem)]">
            {filteredStores.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <MapPin className="h-16 w-16 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {t.noStores}
                </p>
              </div>
            ) : (
              filteredStores.map((store) => (
                <div key={store.id} id={`store-${store.id}`}>
                  <RestaurantCard
                    id={store.id}
                    name={store.name}
                    cuisine={store.address}
                    rating={store.rating}
                    deliveryTime={userLocation ? `${store.calculatedDistance} ${t.distance}` : store.distance || ''}
                    distance=""
                    imageUrl={store.imageUrl}
                    isOpen={store.isOpen}
                    onClick={() => {
                      onStoreSelect(store.id);
                      onClose();
                    }}
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
